<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  spotifyUrl: {
    type: String,
    required: false
  },
  appleMusicUrl: {
    type: String,
    required: false
  },
  youtubeUrl: {
    type: String,
    required: false
  },
})

const hasLinks = computed(() => props.spotifyUrl || props.appleMusicUrl || props.youtubeUrl)

const menuOpen = ref(false)
const menuButtonRef = ref(null)
const menuRef = ref(null)

function toggleMenu(e) {
  e.stopPropagation()
  menuOpen.value = !menuOpen.value
}

function closeMenu() {
  menuOpen.value = false
}

function handleClickOutside(e) {
  if (menuOpen.value && menuRef.value && !menuRef.value.contains(e.target) && !menuButtonRef.value?.contains(e.target)) {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div v-if="hasLinks" class="track-external-links">
    <button
      type="button"
      class="track-external-links__button"
      aria-label="Meer opties"
      :aria-expanded="menuOpen"
      aria-haspopup="true"
      ref="menuButtonRef"
      @click="toggleMenu"
    >
      <span class="track-external-links__dots" aria-hidden="true">
        <span class="track-external-links__dot"></span>
        <span class="track-external-links__dot"></span>
        <span class="track-external-links__dot"></span>
      </span>
    </button>
    <div v-show="menuOpen" class="track-external-links__dropdown" ref="menuRef" role="menu">
      <a
        v-if="spotifyUrl"
        :href="spotifyUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="track-external-links__item"
        role="menuitem"
      >
        <span class="track-external-links__icon track-external-links__icon--spotify" aria-hidden="true"></span>
        Open in Spotify
        <span class="track-external-links__external" aria-hidden="true"></span>
      </a>
      <a
        v-if="appleMusicUrl"
        :href="appleMusicUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="track-external-links__item"
        role="menuitem"
      >
        <span class="track-external-links__icon track-external-links__icon--apple" aria-hidden="true"></span>
        Open in Apple Music
        <span class="track-external-links__external" aria-hidden="true"></span>
      </a>
      <a
        v-if="youtubeUrl"
        :href="youtubeUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="track-external-links__item"
        role="menuitem"
      >
        <span class="track-external-links__icon track-external-links__icon--youtube" aria-hidden="true"></span>
        Open in YouTube
        <span class="track-external-links__external" aria-hidden="true"></span>
      </a>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.track-external-links {
  position: relative;

  &__button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    color: rgb(var(--color-primary-text));
    border-radius: 4px;
    transition: background-color 0.15s;

    &:hover {
      background-color: rgb(var(--color-element));
    }
  }

  &__dots {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
  }

  &__dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: currentColor;
  }

  &__dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 0.25rem;
    min-width: 15rem;
    padding: 0.25rem 0;
    background: rgb(var(--color-element-light, 255 255 255));
    border: 1px solid rgb(var(--color-element-dark));
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 10;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;
    font-weight: 600;
    text-transform: uppercase;
    color: rgb(var(--color-primary));
    text-decoration: none;
    transition: background-color 0.15s;

    &:hover {
      background-color: rgb(var(--color-element));
    }
  }

  &__icon {
    flex: 0 0 auto;
    width: 1.25rem;
    height: 1.25rem;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    color: currentColor;

    &--spotify {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor'%3E%3Cpath d='M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z'/%3E%3C/svg%3E");
    }

    &--apple {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor'%3E%3Cpath d='M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z'/%3E%3C/svg%3E");
    }

    &--youtube {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor'%3E%3Cpath d='M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z'/%3E%3C/svg%3E");
    }
  }

  &__external {
    margin-left: auto;
    width: 0.75rem;
    height: 0.75rem;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2'%3E%3Cpath d='M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6'/%3E%3Cpolyline points='15 3 21 3 21 9'/%3E%3Cline x1='10' y1='14' x2='21' y2='3'/%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
    opacity: 0.7;
  }
}
</style>
